import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { type OutlinedInputProps } from '@material-ui/core/OutlinedInput';

/**
 * Hook to manage local input state for inputs.
 * This fixes a cursor position issue in MUI v4's inputs where
 * the cursor jumps to the end on each keystroke.
 */
export const useCursorPositionFix = (
  value: OutlinedInputProps['value'],
  onChange: OutlinedInputProps['onChange']
) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const isInternalChange = useRef(false);

  // Sync from parent only when it's an external change
  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalValue(value ?? '');
    }
    isInternalChange.current = false;
  }, [value]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      isInternalChange.current = true;
      setLocalValue(e.target.value);
      onChange?.(e as ChangeEvent<HTMLInputElement>);
    },
    [onChange]
  );

  return { value: localValue, onChange: handleChange };
};
